import styled, { css, keyframes } from "styled-components";
import { DotState } from "./types";
import { radius } from "@libertify/ui";

// ─── Keyframes

export const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const pulseV2 = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

export const slideReveal = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

export const slideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(24px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

export const popIn = keyframes`
  from { opacity: 0; transform: scale(0.82) translateY(4px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

const titleReveal = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const thinkingBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30%            { transform: translateY(-6px); opacity: 1; }
`;

// ─── Layout

export const Page = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const StickyHeader = styled.header`
  flex-shrink: 0;
  padding: 32px 48px 20px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;
export const HeadingRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
`;

export const HeadingBlack = styled.h1`
  margin: 0;
  font-size: 36px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.8px;
  line-height: 1.15;
`;

export const HeadingTeal = styled.span`
  font-size: 36px;
  font-weight: 800;
  color: #14b8a6;
  letter-spacing: -0.8px;
`;

export const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 32px 40px 0;
`;

export const WhiteCard = styled.div`
  background: white;
  border-radius: ${radius.box}px ${radius.box}px 0 0;
  padding: 40px;
  margin: 0 auto;
  width: 100%;
  min-height: calc(100vh - 64px);
`;

export const ContentWrapper = styled.div`
  width: 100%;
  padding-bottom: 50vh;
`;

// ─── Timeline

export const StepRow = styled.div`
  display: flex;
  gap: 12px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

export const TimelineCol = styled.div`
  width: 18px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const TimelineDot = styled.div<{ $state: DotState }>`
  width: 7px;
  height: 7px;
  border-radius: ${radius.circle};
  flex-shrink: 0;
  margin-top: 5px;
  background: ${(p) => (p.$state === "pending" ? "#d1d5db" : "#14b8a6")};
`;

export const TimelineTrack = styled.div<{ $state: DotState }>`
  flex: 1;
  width: 1px;
  margin-top: 4px;
  min-height: 20px;
  background: ${(p) =>
    p.$state === "pending"
      ? "repeating-linear-gradient(to bottom, #d1d5db 0, #d1d5db 4px, transparent 4px, transparent 8px)"
      : "#6f747f"};
`;

export const StepContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StepMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
`;

export const StepTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.3px;
  animation: ${titleReveal} 0.3s ease 0.3s both;
`;

export const Skeleton = styled.div`
  margin-left: 18px;
  border-radius: ${radius.box}px;
  height: 72px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

// ─── Dialog Box

export const DialogBox = styled.div`
  border: 1px solid #f3f4f6;
  border-radius: ${radius.box}px;
  overflow: hidden;
  margin-left: 18px;
  animation: ${fadeSlideIn} 0.25s ease both;
`;

export const DialogTop = styled.div<{ $clickable: boolean; $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  transition: background 0.15s;

  ${(p) =>
    p.$clickable &&
    css`
      &:hover {
        background: #f9fafb;
      }
    `}
`;

export const DescriptionText = styled.span`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  flex: 1;
`;

export const TimerBadge = styled.span<{ $done?: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$done ? "#9ca3af" : "#14b8a6")};
  white-space: nowrap;
  flex-shrink: 0;
  align-self: center;
  ${(p) =>
    !p.$done &&
    css`
      animation: ${pulseV2} 1.5s ease infinite;
    `}
`;

export const ChevronBtn = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 2px;
  color: #9ca3af;
`;

export const DialogThinking = styled.div<{ $active?: boolean }>`
  border-top: 1px solid #f3f4f6;
  background: white;
  padding: 12px 16px;
  animation: ${fadeSlideIn} 0.2s ease both;
`;

export const ThinkingText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.7;
  text-align: justify;
`;

const colorCycle = keyframes`
  0%, 100% { color: #9ca3af; }
  50%      { color: #14b8a6; }
`;

export const StoryboardWarningText = styled(ThinkingText)`
  animation: ${colorCycle} 3s ease-in-out infinite;
`;

export const StepSpacer = styled.div`
  height: 32px;
`;

export const ThinkingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 20px 0 20px 18px;
  padding: 0 2px;
`;

export const TDot = styled.span<{ $delay: number }>`
  width: 7px;
  height: 7px;
  border-radius: ${radius.circle};
  background: #14b8a6;
  display: inline-block;
  animation: ${thinkingBounce} 1.2s ease infinite;
  animation-delay: ${(p) => p.$delay}ms;
`;

export const TLabel = styled.span<{ $visible: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: #14b8a6;
  font-style: italic;
  margin-left: 8px;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;
